import { getShopIdFromLocation } from 'util/appUtils';
import {
  MERCHANDISING_READ_ACTIONS,
  MerchandisingApiError,
  createMerchandisingApi,
} from './merchandisingApi';

const shopPath = shopId => `/system/shop/${encodeURIComponent(shopId)}/merchandising/actions`;

// KeaWebServiceClient's async request-promise wrapper hides its cancellation
// handle. This one read uses its public connection config with native fetch.
const abortableSourceRead = async (client, path, body, signal, timeout) => {
  const controller = new AbortController();
  const cancel = () => controller.abort();
  const throwIfAborted = () => {
    if (controller.signal.aborted) {
      throw Object.assign(new Error('Selection source preview cancelled.'), { name: 'AbortError' });
    }
  };
  if (signal.aborted) cancel();
  else signal.addEventListener('abort', cancel);
  const timer = setTimeout(cancel, timeout);
  try {
    const response = await fetch(`${client.baseUrl.replace(/\/?$/, '/')}${path.replace(/^\//, '')}`, {
      method: 'POST', credentials: 'same-origin',
      headers: {
        'Content-Type': 'application/json', ...client.extraHeaders,
        ...(client.secret ? { 'X-KEA-SECRET': client.secret } : {}),
      },
      body: JSON.stringify(body), signal: controller.signal,
    });
    throwIfAborted();
    // Recover an expired session even when a proxy returned an HTML login page.
    if (response.status === 401 && typeof client.handleAuthorizationFailure === 'function') {
      client.handleAuthorizationFailure({ statusCode: 401 });
    }
    const envelope = await response.json();
    throwIfAborted();
    if (!response.ok && response.status !== 401 && typeof client.handleAuthorizationFailure === 'function') {
      client.handleAuthorizationFailure({ statusCode: response.status, body: envelope });
    }
    if (!response.ok && envelope?.result !== 'error') {
      throw new MerchandisingApiError({
        code: `HTTP_${response.status}`,
        message: envelope?.message || 'Could not load matching products.',
      });
    }
    return envelope;
  } catch (error) {
    if (error.name === 'AbortError') {
      throw new MerchandisingApiError({
        code: 'REQUEST_ABORTED', message: 'Selection source preview cancelled.', cause: error,
      });
    }
    throw error;
  } finally {
    clearTimeout(timer);
    signal.removeEventListener('abort', cancel);
  }
};

/** Cloud/web-service transport for the shared merchandising.v1 action port. */
export function createLegacyMerchandisingApi({ client, shopId } = {}) {
  let resolvedClient = client || null;
  const getClient = () => {
    if (!resolvedClient) {
      // eslint-disable-next-line global-require
      resolvedClient = require('util/appUtils').wsClient;
    }
    return resolvedClient;
  };
  const getShopId = () => shopId || getShopIdFromLocation();

  return createMerchandisingApi({
    send: (action, payload, timeout, { signal } = {}) => {
      const currentClient = getClient();
      const path = `${shopPath(getShopId())}/${MERCHANDISING_READ_ACTIONS.has(action) ? 'read' : 'write'}`;
      const body = { action, payload };
      if (action === 'merchandising.preview.selection_source' && signal
        && typeof currentClient.baseUrl === 'string' && typeof fetch === 'function') {
        return abortableSourceRead(currentClient, path, body, signal, timeout);
      }
      return currentClient.prepareRequest('POST', path, { body, ...(signal ? { signal } : {}) });
    },
  });
}
